/**
 * Apollo Error Digest Logger
 *
 * This utility reduces log pollution by aggregating Apollo timeout, connection,
 * and network errors into a single digest report every 3 hours.
 *
 * Features:
 * - Counts errors instead of logging each individual occurrence
 * - Reports digest every 3 hours with error counts
 * - Auto-initializes on first error
 * - Auto-cleanup on app shutdown
 * - Development utilities for testing and debugging
 *
 * Usage:
 * ```ts
 * import { logError, initializeDigestLogger } from './digest-logger';
 *
 * // Initialize (optional - auto-initializes on first error)
 * initializeDigestLogger();
 *
 * // Log errors instead of console.log/error
 * logError('timeout');
 * logError('connection');
 * logError('network');
 * ```
 *
 * Development utilities (available in dev mode):
 * ```ts
 * // In browser console:
 * window.apolloDigestLogger.getDigestInfo() // Get current state
 * window.apolloDigestLogger.simulateErrors({timeout: 5, connection: 2}) // Test
 * window.apolloDigestLogger.flushDigest() // Manual report
 * window.apolloDigestLogger.setCustomInterval(30000) // 30s interval for testing
 * ```
 *
 * Example output:
 * [Apollo Error Digest - 3h] Total: 47 errors | Timeouts: 23, Connections: 15, Network: 9
 */

/* eslint-disable no-console */

interface ErrorDigest {
	timeoutErrors: number;
	connectionErrors: number;
	networkErrors: number;
	lastReset: number;
	errors: Array<{ type: string; timestamp: number; source?: string }>;
}

let globalErrorDigest: ErrorDigest = {
	timeoutErrors: 0,
	connectionErrors: 0,
	networkErrors: 0,
	lastReset: Date.now(),
	errors: [],
};

let digestIntervalId: NodeJS.Timeout | null = null;

const DIGEST_INTERVAL = 3 * 60 * 60 * 1000; // 3 hours in milliseconds

/**
 * Initialize the digest logger if not already initialized
 * This will log a summary of errors every 3 hours
 */
export const initializeDigestLogger = (): void => {
	if (!digestIntervalId) {
		digestIntervalId = setInterval(() => {
			const { timeoutErrors, connectionErrors, networkErrors, lastReset } = globalErrorDigest;
			const totalErrors = timeoutErrors + connectionErrors + networkErrors;

			if (totalErrors > 0) {
				const timePeriod = Math.round((Date.now() - lastReset) / (60 * 60 * 1000)); // hours
				const digestReport = {
					period: `${timePeriod}h`,
					summary: {
						total: totalErrors,
						timeouts: timeoutErrors,
						connections: connectionErrors,
						network: networkErrors,
					},
					errors: globalErrorDigest.errors,
					timestamp: new Date().toISOString(),
				};

				console.log(`[Apollo Error Digest] ${JSON.stringify(digestReport)}`);
			}

			// Reset counters for next period
			globalErrorDigest = {
				timeoutErrors: 0,
				connectionErrors: 0,
				networkErrors: 0,
				lastReset: Date.now(),
				errors: [],
			};
		}, DIGEST_INTERVAL);

		// Log initialization in development
		if (process.env.NODE_ENV === "development") {
			console.log("[Apollo Digest Logger] Initialized - will report errors every 3 hours");
		}
	}
};

/**
 * Log an error to the digest counter
 * @param type - The type of error to log
 */
export const logError = (type: "timeout" | "connection" | "network", source?: string): void => {
	// Add to error array
	globalErrorDigest.errors.push({
		type,
		timestamp: Date.now(),
		source,
	});

	// Update counters
	switch (type) {
		case "timeout":
			globalErrorDigest.timeoutErrors++;
			break;
		case "connection":
			globalErrorDigest.connectionErrors++;
			break;
		case "network":
			globalErrorDigest.networkErrors++;
			break;
		default:
			console.warn(`[Apollo Digest Logger] Unknown error type: ${type}`);
	}
};

/**
 * Get current error counts (useful for debugging)
 */
export const getCurrentDigest = (): Readonly<ErrorDigest> => {
	return { ...globalErrorDigest };
};

/**
 * Force log current digest and reset counters
 * Useful for testing or manual reporting
 */
export const flushDigest = (): void => {
	const { timeoutErrors, connectionErrors, networkErrors, lastReset } = globalErrorDigest;
	const totalErrors = timeoutErrors + connectionErrors + networkErrors;

	if (totalErrors > 0) {
		const timePeriod = Math.round((Date.now() - lastReset) / (60 * 60 * 1000)); // hours
		const digestReport = {
			period: `Manual Flush - ${timePeriod}h`,
			summary: {
				total: totalErrors,
				timeouts: timeoutErrors,
				connections: connectionErrors,
				network: networkErrors,
			},
			errors: globalErrorDigest.errors,
			timestamp: new Date().toISOString(),
		};

		console.log(`[Apollo Error Digest] ${JSON.stringify(digestReport)}`);
	} else {
		const digestReport = {
			period: "Manual Flush - 0h",
			summary: { total: 0, timeouts: 0, connections: 0, network: 0 },
			errors: [],
			timestamp: new Date().toISOString(),
		};

		console.log(`[Apollo Error Digest] ${JSON.stringify(digestReport)}`);
	}

	// Reset counters
	globalErrorDigest = {
		timeoutErrors: 0,
		connectionErrors: 0,
		networkErrors: 0,
		lastReset: Date.now(),
		errors: [],
	};
};

/**
 * Cleanup the digest logger (useful for testing or app shutdown)
 */
export const cleanupDigestLogger = (): void => {
	if (digestIntervalId) {
		clearInterval(digestIntervalId);
		digestIntervalId = null;

		if (process.env.NODE_ENV === "development") {
			console.log("[Apollo Digest Logger] Cleaned up");
		}
	}
};

// Auto-cleanup on window/process exit (browser/Node.js)
if (typeof window !== "undefined") {
	// Browser environment
	window.addEventListener("beforeunload", cleanupDigestLogger);
} else if (typeof process !== "undefined") {
	// Node.js environment
	process.on("exit", cleanupDigestLogger);
	process.on("SIGINT", cleanupDigestLogger);
	process.on("SIGTERM", cleanupDigestLogger);
}

/**
 * Developer utilities for debugging and testing
 * Only available in development mode
 */
export const devUtils = {
	/**
	 * Get detailed digest information
	 */
	getDigestInfo: (): {
		digest: Readonly<ErrorDigest>;
		nextFlushIn: number;
		isActive: boolean;
	} => {
		const nextFlushIn = digestIntervalId ? DIGEST_INTERVAL - (Date.now() - globalErrorDigest.lastReset) : 0;

		return {
			digest: getCurrentDigest(),
			nextFlushIn: Math.max(0, nextFlushIn),
			isActive: digestIntervalId !== null,
		};
	},

	/**
	 * Simulate errors for testing
	 */
	simulateErrors: (counts: { timeout?: number; connection?: number; network?: number }): void => {
		if (process.env.NODE_ENV !== "development") {
			console.warn("[Apollo Digest Logger] simulateErrors only available in development");
			return;
		}

		const { timeout = 0, connection = 0, network = 0 } = counts;

		for (let i = 0; i < timeout; i++) logError("timeout");
		for (let i = 0; i < connection; i++) logError("connection");
		for (let i = 0; i < network; i++) logError("network");

		console.log(`[Apollo Digest Logger] Simulated ${timeout + connection + network} errors`);
	},

	/**
	 * Reset digest counters manually
	 */
	resetCounters: (): void => {
		if (process.env.NODE_ENV !== "development") {
			console.warn("[Apollo Digest Logger] resetCounters only available in development");
			return;
		}

		globalErrorDigest = {
			timeoutErrors: 0,
			connectionErrors: 0,
			networkErrors: 0,
			lastReset: Date.now(),
			errors: [],
		};

		console.log("[Apollo Digest Logger] Counters reset");
	},

	/**
	 * Set custom digest interval (for testing)
	 */
	setCustomInterval: (intervalMs: number): void => {
		if (process.env.NODE_ENV !== "development") {
			console.warn("[Apollo Digest Logger] setCustomInterval only available in development");
			return;
		}

		cleanupDigestLogger();

		digestIntervalId = setInterval(() => {
			const { timeoutErrors, connectionErrors, networkErrors, lastReset } = globalErrorDigest;
			const totalErrors = timeoutErrors + connectionErrors + networkErrors;

			if (totalErrors > 0) {
				const timePeriod = Math.round((Date.now() - lastReset) / (60 * 1000)); // minutes
				console.log(
					`[Apollo Error Digest - Custom ${timePeriod}m] Total: ${totalErrors} errors | ` +
						`Timeouts: ${timeoutErrors}, Connections: ${connectionErrors}, Network: ${networkErrors}`,
				);
			}

			globalErrorDigest = {
				timeoutErrors: 0,
				connectionErrors: 0,
				networkErrors: 0,
				lastReset: Date.now(),
				errors: [],
			};
		}, intervalMs);

		console.log(`[Apollo Digest Logger] Custom interval set to ${intervalMs}ms`);
	},
};

// Expose dev utilities globally in development
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(window as any).apolloDigestLogger = devUtils;
	console.log("[Apollo Digest Logger] Dev utilities available at window.apolloDigestLogger");
}
